import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getProjectTypeInfoKey } from './projectTypeInfo';

const HOVER_DELAY_MS = 300;

export function ProjectTypeInfoTooltip({ projectType, label, t }) {
	const infoKey = getProjectTypeInfoKey(projectType);
	const [open, setOpen] = useState(false);
	const timerRef = useRef(null);
	const wrapRef = useRef(null);

	const clearTimer = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const showDelayed = useCallback(() => {
		clearTimer();
		timerRef.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS);
	}, [clearTimer]);

	const hide = useCallback(() => {
		clearTimer();
		setOpen(false);
	}, [clearTimer]);

	const toggleOpen = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setOpen((value) => !value);
	}, []);

	useEffect(() => {
		if (!open) return undefined;
		const onDocClick = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('click', onDocClick, true);
		return () => document.removeEventListener('click', onDocClick, true);
	}, [open]);

	useEffect(() => () => clearTimer(), [clearTimer]);

	if (!infoKey) {
		return <span className="header-project-type">{label}</span>;
	}

	const title = t(`project.typeInfo.${infoKey}.title`);
	const intro = t(`project.typeInfo.${infoKey}.intro`);
	const split = t(`project.typeInfo.${infoKey}.split`);
	const consult = t(`project.typeInfo.${infoKey}.consult`);
	const reply = t(`project.typeInfo.${infoKey}.reply`);
	const billingTitle = t('project.typeInfo.billingTitle');
	const billing = t('project.typeInfo.billing');

	return (
		<span
			ref={wrapRef}
			className="header-project-type-wrap"
			onMouseEnter={showDelayed}
			onMouseLeave={hide}
		>
			<span
				className="header-project-type header-project-type--interactive"
				role="button"
				tabIndex={0}
				aria-label={title}
				aria-expanded={open}
				onClick={toggleOpen}
				onFocus={showDelayed}
				onBlur={hide}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						toggleOpen(e);
					}
				}}
			>
				{label}
			</span>
			{open && (
				<div className="header-project-type-popover" role="tooltip">
					<div className="header-project-type-popover-title">{title}</div>
					<p>{intro}</p>
					<p>{split}</p>
					{consult && <p>{consult}</p>}
					{reply && <p>{reply}</p>}
					<div className="header-project-type-popover-billing">
						<div className="header-project-type-popover-billing-title">{billingTitle}</div>
						<p>{billing}</p>
					</div>
				</div>
			)}
		</span>
	);
}
